import React from 'react';
import { useAdVocalizeCore } from '../context/AdVocalizeCore';
import { Target, ChevronRight, Settings, X, ShieldCheck, Key, LogOut, Eye, EyeOff, Trash2, Plus, RefreshCw, Activity, RotateCcw } from 'lucide-react';

export const AV_T = {
  bg: 'bg-[#020617]',
  bg2: 'bg-[#080e1e]',
  bg3: 'bg-[#0d1526]',
  panel: 'bg-white/5 backdrop-blur-xl',
  panelHi: 'bg-white/10 backdrop-blur-2xl',
  edge: 'border-white/10',
  edgeHi: 'border-white/20',
  indigo: '#6366f1',
  spectrum: 'bg-gradient-to-br from-[#818cf8] via-[#a855f7] to-[#f97316]',
  ink: 'text-[#f8fafc]',
  inkSoft: 'text-[#f8fafc]/70',
  inkDim: 'text-[#f8fafc]/50',
  muted: 'text-slate-400',
};


export const AppShell = ({ children, heading, trailing, currentStep = 1 }) => {
  const {
    usage, localKey, user, signOut, runDiagnostic, resetStudio,
    apiKeys, activeKeyId, addApiKey, removeApiKey, selectApiKey, updateApiKeyMeta, testApiKey,
    preferredEngine, setPreferredEngine,
  } = useAdVocalizeCore();

  const effectiveKey = preferredEngine === 'byok' ? localKey : '';

  const [showSettings, setShowSettings] = React.useState(false);
  const [settingsTab, setSettingsTab]   = React.useState('keys'); // 'keys' | 'diagnostics'
  const [showUserMenu, setShowUserMenu] = React.useState(false);
  const [visibleKeys, setVisibleKeys]   = React.useState(new Set());
  const [showAddForm, setShowAddForm]   = React.useState(false);
  const [addLabel, setAddLabel]         = React.useState('');
  const [addValue, setAddValue]         = React.useState('');
  const [addVisible, setAddVisible]     = React.useState(false);
  const [diagResults, setDiagResults]   = React.useState(null);
  const [isDiagRunning, setIsDiagRunning] = React.useState(false);
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowUserMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleKeyVisibility = (id) => {
    setVisibleKeys(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleAddKey = () => {
    if (!addValue.trim()) return;
    const id = addApiKey(addLabel || 'API Key', addValue.trim());
    selectApiKey(id);
    setAddLabel(''); setAddValue(''); setAddVisible(false); setShowAddForm(false);
  };

  const handleResetStudio = () => {
    if (window.confirm('Reset studio? All unsaved takes and scripts will be lost.')) {
      resetStudio();
      setShowSettings(false);
    }
  };

  const handleDiagnostic = async () => {
    setIsDiagRunning(true);
    setDiagResults(null);
    try {
      const res = await runDiagnostic();
      setDiagResults(res);
      // Update active key status from diagnostic result
      if (res.byok?.status && activeKeyId) {
        updateApiKeyMeta(activeKeyId, { status: res.byok.status, latencyMs: res.byok.latencyMs });
      }
    } catch (e) {
      setDiagResults({ vertex: { status: 'error', error: e.message }, byok: { status: 'error' } });
    } finally {
      setIsDiagRunning(false);
    }
  };

  const statusColor = (s) => s === 'ok' ? 'bg-emerald-400' : s === 'invalid_key' ? 'bg-red-400' : s === 'error' ? 'bg-orange-400' : s === 'checking' ? 'bg-indigo-400 animate-pulse' : 'bg-slate-600';
  const maskKey = (v) => v ? `${v.slice(0, 6)}${'•'.repeat(14)}${v.slice(-4)}` : '';

  return (
    <div className={`min-h-screen ${AV_T.bg} ${AV_T.ink} font-['Plus_Jakarta_Sans'] relative overflow-hidden`}>
      {/* Ambient Glows */}
      <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-indigo-500/10 blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full bg-purple-500/10 blur-[120px] pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" 
           style={{ backgroundImage: `linear-gradient(#6366f1 1px, transparent 1px), linear-gradient(90deg, #6366f1 1px, transparent 1px)`, backgroundSize: '52px 52px' }} />

      <div className="relative z-10">
        {/* Top Bar */}
        <nav className="h-16 px-8 flex items-center justify-between border-b border-white/10 bg-[#020617]/80 backdrop-blur-3xl sticky top-0 z-50">
          <div className="flex items-center gap-3">
            <span className={`w-8 h-8 rounded-lg ${AV_T.spectrum} flex items-center justify-center text-white font-black text-lg italic shadow-lg shadow-indigo-500/20`}>v</span>
            <span className="font-extrabold text-lg tracking-tight">
              AdVocalize <span className="bg-gradient-to-r from-[#818cf8] via-[#a855f7] to-[#f97316] bg-clip-text text-transparent">Global</span>
            </span>
          </div>

          <div className="flex items-center gap-3">
            {/* Downloads balance */}
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-white/[0.04] border border-white/[0.08] rounded-lg">
              <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Downloads</span>
              <span className={`text-xs font-black ${effectiveKey ? 'text-emerald-400' : usage.creditsRemaining > 0 ? 'text-white' : 'text-orange-400'}`}>
                {effectiveKey ? '∞' : usage.creditsRemaining}
              </span>
            </div>

            {/* Engine toggle pill */}
            <div className="hidden sm:flex items-center bg-white/[0.04] border border-white/[0.08] rounded-lg p-0.5 gap-px">
              <button
                onClick={() => setPreferredEngine('managed')}
                title={usage.creditsRemaining === 0 ? 'No managed sessions remaining' : 'Use managed AI'}
                className={`px-3 py-1.5 rounded text-[9px] font-black uppercase tracking-widest transition-all ${
                  preferredEngine === 'managed'
                    ? usage.creditsRemaining > 0 ? 'bg-indigo-600 text-white shadow' : 'bg-orange-500/20 text-orange-400'
                    : 'text-slate-500 hover:text-white'
                }`}
              >
                {preferredEngine === 'managed' ? `Managed · ${usage.creditsRemaining === 0 ? '0' : usage.creditsRemaining}` : 'Managed'}
              </button>
              <button
                onClick={() => localKey && setPreferredEngine('byok')}
                title={!localKey ? 'Add an API key in Settings first' : 'Use your own API key'}
                className={`px-3 py-1.5 rounded text-[9px] font-black uppercase tracking-widest transition-all ${
                  preferredEngine === 'byok'
                    ? 'bg-emerald-600 text-white shadow'
                    : localKey ? 'text-slate-500 hover:text-white' : 'text-slate-700 cursor-not-allowed'
                }`}
              >
                BYOK{preferredEngine === 'byok' ? ' · ∞' : ''}
              </button>
            </div>

            {/* Settings shortcut */}
            <button
              onClick={() => setShowSettings(true)}
              className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-all"
            >
              <Settings className="w-4 h-4" />
            </button>

            {/* Avatar + dropdown */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowUserMenu(v => !v)}
                className="flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-all"
              >
                <span className={`w-6 h-6 rounded-lg flex items-center justify-center text-[11px] font-black text-white shrink-0 ${AV_T.spectrum}`}>
                  {user?.email?.[0].toUpperCase() || 'S'}
                </span>
                <ChevronRight className={`w-3.5 h-3.5 text-white/40 transition-transform duration-200 ${showUserMenu ? 'rotate-90' : ''}`} />
              </button>

              {showUserMenu && (
                <div className="absolute top-full right-0 mt-2 w-60 bg-[#080e1e] border border-white/10 rounded-2xl shadow-2xl z-[60] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                  {/* User identity */}
                  <div className="p-4 border-b border-white/[0.06]">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-black text-white shrink-0 ${AV_T.spectrum}`}>
                        {user?.email?.[0].toUpperCase() || 'S'}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-black text-white truncate">{user?.email}</p>
                        <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mt-0.5">AppSumo · Pro</p>
                      </div>
                    </div>
                  </div>
                  {/* Stats */}
                  <div className="px-4 py-3 border-b border-white/[0.06] space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Downloads</span>
                      <span className={`text-[9px] font-black ${effectiveKey ? 'text-emerald-400' : usage.creditsRemaining > 0 ? 'text-white' : 'text-orange-400'}`}>
                        {effectiveKey ? 'Unlimited' : `${usage.creditsRemaining} remaining`}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Engine</span>
                      <span className={`text-[9px] font-black ${preferredEngine === 'byok' ? 'text-emerald-400' : usage.creditsRemaining > 0 ? 'text-indigo-400' : 'text-orange-400'}`}>
                        {preferredEngine === 'byok' ? 'BYOK (API Key)' : 'Managed'}
                      </span>
                    </div>
                  </div>
                  {/* Actions */}
                  <div className="p-2">
                    <button
                      onClick={() => { setShowUserMenu(false); setShowSettings(true); }}
                      className="w-full px-3 py-2.5 flex items-center gap-3 rounded-xl hover:bg-white/5 transition-all text-left group"
                    >
                      <Settings className="w-4 h-4 text-slate-500 group-hover:text-white transition-colors" />
                      <span className="text-xs font-black text-slate-400 group-hover:text-white transition-colors">Studio Settings</span>
                    </button>
                    <button
                      onClick={() => { setShowUserMenu(false); signOut(); }}
                      className="w-full px-3 py-2.5 flex items-center gap-3 rounded-xl hover:bg-red-500/10 transition-all text-left group"
                    >
                      <LogOut className="w-4 h-4 text-slate-500 group-hover:text-red-400 transition-colors" />
                      <span className="text-xs font-black text-slate-400 group-hover:text-red-400 transition-colors">Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </nav>

        {/* Compact Step Strip */}
        <div className="h-8 px-8 flex items-center gap-5 border-b border-white/[0.06] bg-black/10">
          <div className="flex items-center gap-2.5">
            <div className="w-[2px] h-3.5 rounded-full bg-indigo-500" />
            <span className="text-[9px] font-black uppercase tracking-[0.35em] text-indigo-400/80">
              {(['UPLOAD','FRAME','VOICE','MIX'])[currentStep - 1] || 'STUDIO'}
            </span>
          </div>
          <div className="flex-1 h-px bg-white/[0.04]" />
          <MiniStepDashes current={currentStep} />
        </div>

        {/* Main Content */}
        <main className="transition-all duration-500">
          {children}
        </main>
      </div>
      
      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-xl bg-black/60">
          <div className="w-full max-w-2xl bg-[#080e1e] border border-white/10 rounded-[2.5rem] shadow-2xl animate-in zoom-in-95 duration-300 flex flex-col max-h-[90vh]">

            {/* Header */}
            <div className="flex items-center justify-between px-8 pt-8 pb-5 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-indigo-500/10 flex items-center justify-center text-indigo-400">
                  <Settings className="w-4.5 h-4.5" />
                </div>
                <h3 className="text-lg font-black italic tracking-tight text-white uppercase">Studio Settings</h3>
              </div>
              <button onClick={() => setShowSettings(false)} className="w-9 h-9 rounded-full hover:bg-white/5 flex items-center justify-center text-slate-500 hover:text-white transition-all">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Tab switcher */}
            <div className="flex mx-8 mb-5 bg-white/5 p-1 rounded-xl border border-white/5 shrink-0">
              {[{ id: 'keys', label: 'API Keys', icon: Key }, { id: 'diagnostics', label: 'Diagnostics', icon: Activity }].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setSettingsTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${settingsTab === tab.id ? 'bg-indigo-600 text-white shadow' : 'text-slate-500 hover:text-white'}`}
                >
                  <tab.icon className="w-3 h-3" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-8 pb-8 space-y-4 custom-scrollbar">

              {/* ── KEYS TAB ── */}
              {settingsTab === 'keys' && (
                <>
                  <p className="text-[10px] text-slate-500 font-medium leading-relaxed">
                    Select your active Gemini API key. Switch freely — the studio uses it immediately.
                    Keys are stored locally on this device only.
                  </p>

                  {/* Key list */}
                  <div className="space-y-2">
                    {apiKeys.length === 0 && (
                      <div className="py-8 border border-dashed border-white/10 rounded-2xl text-center">
                        <p className="text-[10px] font-black text-slate-600 uppercase tracking-widest">No keys added yet</p>
                      </div>
                    )}
                    {apiKeys.map(k => (
                      <div
                        key={k.id}
                        onClick={() => selectApiKey(k.id)}
                        className={`flex items-center gap-3 p-3 rounded-2xl border cursor-pointer transition-all ${activeKeyId === k.id ? 'border-indigo-500/40 bg-indigo-500/5' : 'border-white/5 bg-white/[0.02] hover:border-white/10'}`}
                      >
                        {/* Radio */}
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${activeKeyId === k.id ? 'border-indigo-400' : 'border-white/20'}`}>
                          {activeKeyId === k.id && <span className="w-2 h-2 rounded-full bg-indigo-400 block" />}
                        </div>

                        {/* Label */}
                        <span className="text-xs font-black text-white w-20 shrink-0 truncate">{k.label}</span>

                        {/* Masked value */}
                        <span className="flex-1 font-mono text-[10px] text-slate-500 truncate min-w-0">
                          {visibleKeys.has(k.id) ? k.value : maskKey(k.value)}
                        </span>

                        {/* Status dot */}
                        <div className={`w-2 h-2 rounded-full shrink-0 ${statusColor(k.status)}`} title={k.status} />
                        {k.latencyMs && k.status === 'ok' && (
                          <span className="text-[9px] font-mono text-slate-600 shrink-0">{k.latencyMs}ms</span>
                        )}

                        {/* Actions */}
                        <button onClick={e => { e.stopPropagation(); testApiKey(k.id); }}
                          className="text-slate-600 hover:text-indigo-400 transition-colors shrink-0 p-1" title="Test key">
                          <RefreshCw className={`w-3 h-3 ${k.status === 'checking' ? 'animate-spin text-indigo-400' : ''}`} />
                        </button>
                        <button onClick={e => { e.stopPropagation(); toggleKeyVisibility(k.id); }}
                          className="text-slate-600 hover:text-white transition-colors shrink-0 p-1">
                          {visibleKeys.has(k.id) ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        </button>
                        <button onClick={e => { e.stopPropagation(); removeApiKey(k.id); }}
                          className="text-slate-700 hover:text-red-400 transition-colors shrink-0 p-1">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Add new key */}
                  {showAddForm ? (
                    <div className="p-4 border border-indigo-500/20 bg-indigo-500/5 rounded-2xl space-y-3 animate-in slide-in-from-top-2">
                      <input
                        value={addLabel}
                        onChange={e => setAddLabel(e.target.value)}
                        placeholder="Label (e.g. Work Key)"
                        className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-xl text-white text-xs font-bold focus:border-indigo-500 outline-none"
                      />
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <input
                            type={addVisible ? 'text' : 'password'}
                            value={addValue}
                            onChange={e => setAddValue(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleAddKey()}
                            placeholder="AIza... Gemini API key"
                            autoFocus
                            className="w-full pl-4 pr-10 py-2.5 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-xs focus:border-indigo-500 outline-none"
                          />
                          <button onClick={() => setAddVisible(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-600 hover:text-white">
                            {addVisible ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                        <button onClick={handleAddKey} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-black text-xs transition-all active:scale-95">Add</button>
                        <button onClick={() => { setShowAddForm(false); setAddLabel(''); setAddValue(''); }} className="px-3 py-2 bg-white/5 border border-white/10 text-slate-400 rounded-xl font-black text-xs hover:bg-white/10 transition-all">✕</button>
                      </div>
                    </div>
                  ) : (
                    apiKeys.length < 5 && (
                      <button onClick={() => setShowAddForm(true)}
                        className="w-full py-3 border border-dashed border-white/10 rounded-2xl text-[10px] font-black text-slate-500 uppercase tracking-widest hover:border-indigo-500/40 hover:text-indigo-400 transition-all flex items-center justify-center gap-2">
                        <Plus className="w-3.5 h-3.5" /> Add API Key
                      </button>
                    )
                  )}

                  {/* Reset Studio */}
                  <div className="pt-2 border-t border-white/5">
                    <button onClick={handleResetStudio}
                      className="w-full py-3 flex items-center justify-center gap-2 border border-red-500/20 bg-red-500/5 rounded-2xl text-[10px] font-black text-red-400/70 uppercase tracking-widest hover:bg-red-500/10 hover:text-red-400 transition-all">
                      <RotateCcw className="w-3.5 h-3.5" /> Reset Studio
                    </button>
                  </div>
                </>
              )}

              {/* ── DIAGNOSTICS TAB ── */}
              {settingsTab === 'diagnostics' && (
                <>
                  <button
                    onClick={handleDiagnostic}
                    disabled={isDiagRunning}
                    className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isDiagRunning ? <><RefreshCw className="w-4 h-4 animate-spin" /> Running health check...</> : <><Activity className="w-4 h-4" /> Run Health Check</>}
                  </button>

                  {diagResults && (
                    <div className="space-y-5 animate-in fade-in slide-in-from-top-4">

                      {/* Status rows — full-width horizontal */}
                      <div className="space-y-2">
                        {[
                          { label: 'Vertex AI',                         d: diagResults.vertex },
                          { label: localKey ? 'BYOK Key' : 'No BYOK key configured', d: diagResults.byok },
                        ].map(({ label, d }) => {
                          const ok    = d?.status === 'ok';
                          const nc    = d?.status === 'not_configured';
                          const inv   = d?.status === 'invalid_key';
                          const quota = d?.status === 'quota_exceeded';
                          const dot   = ok ? 'bg-emerald-400' : nc ? 'bg-slate-600' : quota ? 'bg-amber-400' : inv ? 'bg-red-400' : 'bg-orange-400';
                          const rowBg = ok ? 'bg-emerald-500/5 border-emerald-500/15' : nc ? 'bg-white/[0.02] border-white/5' : quota ? 'bg-amber-500/5 border-amber-500/20' : 'bg-red-500/5 border-red-500/20';
                          const textCl= ok ? 'text-emerald-400' : nc ? 'text-slate-500' : quota ? 'text-amber-400' : inv ? 'text-red-400' : 'text-orange-400';
                          const statusLabel = ok ? 'OK' : nc ? 'Not configured' : quota ? 'Quota limit' : inv ? 'Invalid key' : 'Error';
                          return (
                            <div key={label} className={`flex items-start gap-3 px-5 py-4 rounded-2xl border ${rowBg}`}>
                              <span className={`mt-[3px] w-2.5 h-2.5 rounded-full shrink-0 ${dot} ${ok ? 'shadow-[0_0_6px_currentColor]' : ''}`} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-3 flex-wrap">
                                  <span className={`text-[11px] font-black uppercase tracking-widest ${textCl}`}>{statusLabel}</span>
                                  <span className="text-[11px] font-black text-white/50 uppercase tracking-widest">{label}</span>
                                  {ok && <span className="text-[10px] font-mono text-slate-500">{d.latencyMs}ms</span>}
                                </div>
                                {d?.error && (
                                  <p className="mt-1 text-[10px] text-slate-400 font-medium leading-relaxed line-clamp-2">{d.error}</p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Model table */}
                      <div className="bg-black/30 border border-white/5 rounded-2xl overflow-hidden">
                        <div className="grid grid-cols-3 px-5 py-3 border-b border-white/5">
                          {['Stage', 'Managed', 'BYOK'].map(h => <span key={h} className="text-[10px] font-black uppercase tracking-widest text-slate-500">{h}</span>)}
                        </div>
                        {[
                          { stage: 'Vision', v: diagResults.models?.vision?.vertex, b: diagResults.models?.vision?.byok },
                          { stage: 'Script', v: diagResults.models?.script?.vertex, b: diagResults.models?.script?.byok },
                          { stage: 'Voice',  v: diagResults.models?.voice?.vertex,  b: diagResults.models?.voice?.byok },
                        ].map(({ stage, v, b }) => (
                          <div key={stage} className="grid grid-cols-3 px-5 py-3 border-b border-white/[0.04] last:border-0">
                            <span className="text-[10px] font-black text-white/70 uppercase tracking-widest">{stage}</span>
                            <span className="text-[10px] font-mono text-indigo-400/80 truncate pr-4">{v}</span>
                            <span className="text-[10px] font-mono text-emerald-400/80 truncate">{b}</span>
                          </div>
                        ))}
                        {diagResults.models?.voice?.byokFallback && (
                          <div className="px-5 py-2.5 bg-white/[0.01] border-t border-white/[0.03]">
                            <span className="text-[9px] font-mono text-slate-600">▾ fallback: {diagResults.models.voice.byokFallback}</span>
                          </div>
                        )}
                      </div>

                      {/* RPM/RPD table */}
                      {diagResults.freeTierLimits && (
                        <div className="bg-black/30 border border-white/5 rounded-2xl overflow-hidden">
                          <div className="grid grid-cols-3 px-5 py-3 border-b border-white/5">
                            {['Model (AI Studio Free)', 'RPM', 'RPD'].map(h => <span key={h} className="text-[10px] font-black uppercase tracking-widest text-slate-500">{h}</span>)}
                          </div>
                          {Object.entries(diagResults.freeTierLimits).map(([model, lim]) => (
                            <div key={model} className="grid grid-cols-3 px-5 py-3 border-b border-white/[0.04] last:border-0">
                              <span className="text-[10px] font-mono text-slate-400 truncate pr-4">{model}</span>
                              <span className="text-[10px] font-black text-orange-400">{lim.rpm}</span>
                              <span className="text-[10px] font-black text-orange-400">{lim.rpd}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

            </div>
          </div>
        </div>
      )}

      {/* Global Footer (Subtle) */}
      <footer className="mt-20 py-10 border-t border-white/5 flex flex-col items-center gap-4 opacity-30">
          <div className="flex items-center gap-3">
              <Target className="w-4 h-4" />
              <p className="text-[10px] font-black uppercase tracking-[0.3em]">AdVocalize Global Studio · V3.0</p>
          </div>
      </footer>
    </div>
  );
};

export const StepIndicator = ({ current = 1, steps = ['UPLOAD', 'FRAME', 'VOICE', 'MIX'] }) => {
  return (
    <div className="inline-flex items-center">
      {steps.map((label, i) => {
        const n = i + 1;
        const active = n === current;
        const done = n < current;
        return (
          <React.Fragment key={label}>
            <div className="flex flex-col items-center gap-2 px-6 group">
              <div className={`
                w-10 h-10 rounded-xl border-2 flex items-center justify-center text-xs font-black transition-all duration-500
                ${active ? 'bg-indigo-600 border-indigo-400 text-white shadow-[0_0_25px_rgba(99,102,241,0.6)] scale-110' : 
                  done ? 'bg-indigo-600/10 border-indigo-500/30 text-indigo-400' : 
                  'bg-white/5 border-white/10 text-slate-600'}
              `}>
                {done ? '✓' : n}
              </div>
              <span className={`text-[10px] font-black tracking-[0.2em] transition-colors ${active ? 'text-white' : done ? 'text-indigo-300/60' : 'text-slate-600'}`}>
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`h-[2px] w-20 mb-6 transition-colors duration-700 ${done ? 'bg-indigo-600' : 'bg-white/5'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export const Waveform = ({ bars = 32, active = false, seed = 1 }) => {
    const heights = React.useMemo(() => {
        let s = (seed * 1664525 + 1013904223) & 0x7fffffff;
        return Array.from({ length: bars }).map(() => {
            s = (s * 1664525 + 1013904223) & 0x7fffffff;
            return 20 + (s % 80);
        });
    }, [bars, seed]);

    return (
        <div className="flex items-center gap-[3px] h-full">
            {heights.map((height, i) => (
                <div
                    key={i}
                    className={`w-1 rounded-full transition-all duration-300 ${active ? 'bg-indigo-400' : 'bg-white/10'}`}
                    style={{ height: `${height}%` }}
                />
            ))}
        </div>
    );
};

export const MiniStepDashes = ({ current, total = 4 }) => (
    <div className="flex items-center gap-1.5">
        {Array.from({ length: total }).map((_, i) => {
            const n = i + 1;
            return (
                <div key={n} className={`h-[3px] rounded-full transition-all duration-500 ${
                    n === current ? 'w-7 bg-indigo-400 shadow-[0_0_8px_rgba(99,102,241,0.9)]' :
                    n < current  ? 'w-5 bg-indigo-500/40' : 'w-3 bg-white/10'
                }`} />
            );
        })}
    </div>
);

export const PlayPill = ({ playing, onClick, size = "md" }) => {
    const s = size === 'sm' ? 'w-8 h-8' : 'w-12 h-12';
    return (
        <button 
            onClick={onClick}
            className={`${s} rounded-full bg-white text-black flex items-center justify-center hover:scale-110 active:scale-95 transition-all shadow-xl`}
        >
            {playing ? (
                <div className="flex gap-1">
                    <div className="w-1 h-3 bg-black rounded-full" />
                    <div className="w-1 h-3 bg-black rounded-full" />
                </div>
            ) : (
                <div className="ml-1 border-y-[6px] border-y-transparent border-l-[10px] border-l-black" />
            )}
        </button>
    );
};
